import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getSubCategoryDueList } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const feeHeadApi = createLookupApi("fee-heads");

/**
 * Direct port of digicampusbd's `fee-report-sub-category-due-list` ("Due
 * List Fee Wise") — for one chosen fee head, every student in a section's
 * due amount for that fee head's calendar-scheduled subheads, split
 * "not-yet-collected" vs. "invoiced but partial" (the same two-bucket total
 * as the already-built Fee Due List Student Wise report). New backend
 * (`FeeReportService.subCategoryDueList`) reuses the exact same due-
 * computation building blocks as `monthWisePayableDueDetails`, scoped to one
 * fee head's own configured months instead of a chosen month subset, and
 * with no group filter (matching the old app's own endpoint signature:
 * academicYear + classConfigId + feeHeadId only). Documented simplification:
 * the old app's per-student `StudentIndividualFeeAssign`/
 * `StudentFeeHeadDiscardInfo` override tables exist in this app but are not
 * folded into this computation yet — see MIGRATION.md.
 */
export default function FeeSubCategoryDueListPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");

  const [rows, setRows] = useState(null);
  const [monthKeys, setMonthKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    feeHeadApi.list().then((heads) => {
      setFeeHeads(heads);
      if (heads.length > 0) setFeeHeadId(String(heads[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setRows(null);
    if (!academicYear || !classConfigId || !feeHeadId) {
      setError("Please select academic year, section, and fee head.");
      return;
    }
    setLoading(true);
    try {
      const data = await getSubCategoryDueList(academicYear, classConfigId, feeHeadId);
      const withTotals = data.filter((r) => r.grandTotal !== 0);
      if (withTotals.length === 0) {
        setError("No Due Found");
      }
      const keys = withTotals.length > 0 ? Object.keys(withTotals[0].dueDetails) : [];
      setMonthKeys(keys);
      setRows(withTotals);
    } catch (err) {
      setError("Failed to load due list.");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = (rows || []).reduce((sum, r) => sum + r.grandTotal, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Due List Fee Wise</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Fee Head
          <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)}>
            {feeHeads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  {monthKeys.map((m) => (
                    <th key={m}>{m}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.identificationId}>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    {monthKeys.map((m) => (
                      <td key={m}>{r.dueDetails[m]}</td>
                    ))}
                    <td>{r.grandTotal}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3 + monthKeys.length} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>{grandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
